import React, { useState, useEffect } from "react";
import axios from "utils/axiosConfig";
import { DataGrid } from "@mui/x-data-grid";
import { Stack, IconButton } from "@mui/material";
import FilterListIcon from "@mui/icons-material/FilterList";
import GeneralUtilityService from "services/GeneralUtilityService";
import { useSelector, useDispatch } from "react-redux";
import { urlService } from "infra/envs";
import companiesColumnHeaders from "features/companies/companiesColumnHeaders";
import useNotifications from "hooks/useNotifications";
import CompaniesTablePagination from "./CompaniesTablePagination";
import { setPage } from "reducers/company/companySlice";
import isEqual from "lodash/isEqual";

export const CompaniesTable = ({ setFilterVisibility }) => {
    const dispatch = useDispatch();
    const [filters, setFilters] = useState({});
    const [paginationData, setPaginationData] = useState({});
    const companiesFiltersData = useSelector(
        (state) => state.companiesSearchFilters,
    );
    const { page } = useSelector((state) => state.companySlice.listing);

    const getCompaniesUrl = urlService.COMPANIES_LISTING_PATH;

    const [isLoading, setIsLoading] = useState(false);
    const [companiesTableRows, setCompaniesTableRows] = useState([]);

    const { renderErrorNotification } = useNotifications();

    async function getCompanies(endpoint, filterData) {
        try {
            setIsLoading(true);

            const clearedFilterData = GeneralUtilityService.clearEmptyKeys({
                ...(filterData || {}),
            });

            const response = await axios.get(endpoint, {
                params: {
                    ...clearedFilterData,
                    subscription: companiesFiltersData?.subscription?.id,
                    page: page,
                },
            });

            const resData = response?.data;
            setPaginationData(resData?.meta);

            const fetchedCompanies = resData?.data;

            setCompaniesTableRows(fetchedCompanies);

            setIsLoading(false);
        } catch (error) {
            console.error("OOPS!" + error.message);
            renderErrorNotification(`${error?.response?.data?.message}`);
        } finally {
            setIsLoading(false);
        }
    }
    useEffect(() => {
        if (page !== 1)
            dispatch(setPage(1));
        else {
            if (!isEqual(filters, companiesFiltersData)) {
                setFilters(companiesFiltersData);
                getCompanies(getCompaniesUrl, companiesFiltersData);
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [companiesFiltersData, dispatch]);

    useEffect(() => {
        getCompanies(getCompaniesUrl, companiesFiltersData);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [page]);

    //cleanup
    useEffect(() => {
        return setIsLoading(false);
    }, []);

    const handleCellClick = (params) => {
        window.open(
            `${urlService.COMPANY_PROFILE_PATH}/${params.id}`,
            "_blank",
        );
    };

    return (
        <>
            <Stack px={2}>
                <Stack alignItems="flex-start">
                    <IconButton
                        aria-label="Collapse or expand list"
                        onClick={() => {
                            setFilterVisibility((visibility) => !visibility);
                        }}
                    >
                        <FilterListIcon />
                    </IconButton>
                </Stack>

                <DataGrid
                    onRowClick={(rowData) => handleCellClick(rowData)}
                    component="table"
                    rowSpacingType="margin"
                    autoHeight
                    rows={companiesTableRows}
                    columns={companiesColumnHeaders}
                    disableColumnMenu
                    hideFooter
                    loading={isLoading}
                    sx={{
                        border: 0,
                        "& .MuiDataGrid-cell:focus,& .MuiDataGrid-columnHeader:focus":
                            {
                                outline: "none",
                                borderColor: "rgba(0,0,0,0)",
                            },
                        "& .MuiDataGrid-row:focus-within": {
                            boxShadow: "none",
                        },
                    }}
                />
                <CompaniesTablePagination
                    isLoading={isLoading}
                    paginationData={paginationData}
                />
            </Stack>
        </>
    );
};

export default CompaniesTable;
